/**
 * RenameWatchlistModal View Component
 *
 * AlertDialog for renaming a watchlist, matching the CustomPrompt
 * visual style with centered heading, subtitle, input, and
 * full-width footer buttons.
 * @module features/scouting/components/RenameWatchlistModal
 */

import { memo } from "react";
import { Platform } from "react-native";
import { FieldErrors } from "react-hook-form";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/atoms/AlertDialog";
import { Pressable } from "@/components/atoms/Pressable";
import {
  FormControl,
  FormControlError,
  FormControlErrorText,
} from "@/components/atoms/FormControl";
import { Heading } from "@/components/atoms/Heading";
import { Input } from "@/components/atoms/Input";
import { Text } from "@/components/atoms/Text";
import { WATCHLIST_NAME_MAX } from "@/features/scouting/utils/watchlistNameRules";
import { ifWeb } from "@/utils/platform";

/** Form values for watchlist name */
interface WatchlistNameFormValues {
  /** Watchlist name */
  name: string;
}

/** Props for RenameWatchlistModalView */
interface RenameWatchlistModalViewProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Form errors */
  readonly errors: FieldErrors<WatchlistNameFormValues>;
  /** Whether form is valid */
  readonly isValid: boolean;
  /** Whether form is submitting */
  readonly isSubmitting: boolean;
  /** Callback to close modal */
  readonly onClose: () => void;
  /** Callback when form is submitted */
  readonly onSubmit: () => void;
  /** Current watchlist name value */
  readonly watchlistName: string;
  /** Callback when watchlist name changes */
  readonly onWatchlistNameChange: (value: string) => void;
  /** Whether tapping the backdrop should dismiss the modal */
  readonly closeOnOverlayClick: boolean;
}

/**
 * Renders the rename watchlist modal using AlertDialog to match
 * the CustomPrompt iOS-native style
 * @param root0 - Component props
 * @param root0.isOpen - Whether the modal is open
 * @param root0.errors - Form errors
 * @param root0.isValid - Whether form is valid
 * @param root0.isSubmitting - Whether form is submitting
 * @param root0.onClose - Close handler
 * @param root0.onSubmit - Submit handler
 * @param root0.watchlistName - Current watchlist name value
 * @param root0.onWatchlistNameChange - Watchlist name change handler
 * @param root0.closeOnOverlayClick - Whether backdrop taps dismiss the modal
 */
const RenameWatchlistModalView = ({
  isOpen,
  errors,
  isValid,
  isSubmitting,
  onClose,
  onSubmit,
  watchlistName,
  onWatchlistNameChange,
  closeOnOverlayClick,
}: RenameWatchlistModalViewProps) => (
  <AlertDialog
    isOpen={isOpen}
    onClose={onClose}
    closeOnOverlayClick={closeOnOverlayClick}
    useRNModal={Platform.OS !== "web"}
    size="sm"
  >
    <AlertDialogBackdrop className="bg-black" />
    <AlertDialogContent className="border-0 bg-surface-base p-0 dark:bg-prompt-background">
      <AlertDialogBody className="p-5 pb-4">
        <Heading
          variant="title"
          className="shrink text-center text-content-primary dark:text-white"
        >
          Rename List
        </Heading>
        <Text
          variant="unstyled"
          className="shrink text-center font-medium text-title text-content-muted"
        >
          Enter a new name for this list.
        </Text>
        <FormControl isInvalid={!!errors.name} className="pt-6">
          <Input
            variant="outline"
            className="h-12 rounded-sm border-outline-default bg-surface-raised dark:border-prompt-border dark:bg-surface-action"
            field={{
              value: watchlistName,
              placeholder: "Enter list name",
              autoCorrect: false,
              spellCheck: false,
              onChangeText: onWatchlistNameChange,
              maxLength: WATCHLIST_NAME_MAX,
              className: "text-title text-content-primary dark:text-white",
            }}
          />
          {errors.name ? (
            <FormControlError>
              <FormControlErrorText>{errors.name.message}</FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
      </AlertDialogBody>
      <AlertDialogFooter className="gap-0 border-t border-outline-default bg-surface-base dark:border-prompt-border dark:bg-prompt-background">
        <Pressable
          onPress={onClose}
          disabled={isSubmitting}
          accessibilityRole="button"
          className="web:hover:bg-surface-muted dark:web:hover:bg-surface-inverse/10 h-14 flex-1 items-center justify-center rounded-none border-r border-outline-default bg-transparent active:bg-surface-strong dark:border-prompt-border dark:active:bg-surface-inverse/15"
        >
          <Text
            variant="unstyled"
            className="text-title-lg text-content-neutral-strong dark:text-content-subtle"
          >
            Cancel
          </Text>
        </Pressable>
        <Pressable
          onPress={onSubmit}
          disabled={!isValid || isSubmitting}
          accessibilityRole="button"
          className="web:hover:bg-surface-muted dark:web:hover:bg-surface-inverse/10 h-14 flex-1 items-center justify-center rounded-none bg-transparent active:bg-surface-strong dark:active:bg-surface-inverse/15"
        >
          <Text
            variant="unstyled"
            className={`${ifWeb("text-title", "text-title-lg")} font-heading ${isValid && !isSubmitting ? "text-needs-semantic-primary-500 dark:text-needs-semantic-primary-400" : "text-content-subtle dark:text-content-neutral-strong"}`}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Text>
        </Pressable>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

RenameWatchlistModalView.displayName = "RenameWatchlistModalView";

export default memo(RenameWatchlistModalView);
